import React, { useEffect, useState, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { transform } from 'ol/proj';
import { Draw } from 'ol/interaction';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Feature } from 'ol';
import { Point, Polygon } from 'ol/geom';
import { Style, Stroke, Fill, Circle as CircleStyle } from 'ol/style';
import { getCenter } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import Tool from '../Tool/Tool';
import Modal from '../Bootstrap/Modal/Modal';
import MouseFollower from '../MouseFollower/MouseFollower';
import IncidenceForm from './IncidenceForm';
import FileUpload from '../FileUpload/FileUpload';
import TabNavigation from '../Bootstrap/Tabs/TabNavigation';
import './AddIncidence.css';

/**
 * Contexts
 */
import { useToolsContext } from '../../context/ToolsContext';
import { useToast } from '../../context/ToastContext';
import MapContext from '../Map/MapContext';
import { useUserSession } from '../../hooks/useUserSession';

/** 
 * Utils 
 */
import { getVisorID } from '../../utils/misc';
import { createIncidence } from '../../services/incidenceService';
import { addIncidenceToLayer } from '../helpers';

const AddIncidence = () => {
    const { t } = useTranslation();
    const { map } = useContext(MapContext);
    const { addToast, closeToast } = useToast();
    const { toolStates, setToolState } = useToolsContext();
    const { session } = useUserSession();

    // Estados del componente
    const [showModal, setShowModal] = useState(false);
    const [showText, setShowText] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [activeTab, setActiveTab] = useState('datos');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [geometryType, setGeometryType] = useState('Point'); // 'Point', 'Polygon'
    const [drawnGeometry, setDrawnGeometry] = useState(null); // Geometría dibujada completa

    // Referencias para Draw y capas
    const drawRef = useRef(null);
    const drawSourceRef = useRef(new VectorSource());
    const drawLayerRef = useRef(null);

    // Configuración de pestañas
    const tabs = [
        { label: t('Data'), value: 'datos' },
        // { label: t('Attachments'), value: 'adjuntos' },
    ];

    // Funciones de manejo de toasts
    const messageIncidenceAddSuccess = (data) => {
        addToast({
            id: 'incidenceAdded',
            show: true,
            title: t('Incidence added!'),
            message: t('The incidence has been saved successfully.'),
            duration: 2000,
        });
        // Crear customEvent para que el panel de incidencias se actualice
        window.dispatchEvent(new CustomEvent('incidence:refresh', {
            detail: { coordinates: data.coordinates }
        }));
    };

    const messageIncidenceAddError = (message) => {
        addToast({
            id: 'incidenceError',
            show: true,
            title: t('Incidence error!'),
            message: message || t('An error occurred while saving the incidence.'),
            duration: 8000,
            variations: 'danger'
        });
    };

    const messageShowSteps = () => {
        const geometryTypeMessages = {
            'Point': t('Click on the map to add a new incidence.'),
            'Polygon': t('Draw a polygon on the map. Double click to finish.')
        };

        addToast({
            id: 'stepsAddIncidence',
            show: true,
            title: t('Steps to follow!'),
            message: (
                <>
                    <p>{t('You are about to add a new incidence.')}</p>
                    <ol>
                        <li>{geometryTypeMessages[geometryType] || t('Click on the map to add a new incidence.')}</li>
                        <li>{t('Fill in the displayed form.')}</li>
                        <li>{t('Click save.')}</li>
                    </ol>
                </>
            ),
            duration: 5000,
        });
    };

    // Función para calcular el centroide de una geometría
    const getCentroidFromGeometry = (geometry) => {
        if (!geometry) return null;
        
        try {
            // Para Point, devolver las coordenadas directamente
            if (geometry instanceof Point) {
                const coords = geometry.getCoordinates();
                return transform(coords, 'EPSG:3857', 'EPSG:4326');
            }
            
            // Para Polygon, usar getInteriorPoint o getCenter del extent
            let centerPoint;
            if (geometry.getInteriorPoint) {
                centerPoint = geometry.getInteriorPoint();
            } else {
                const extent = geometry.getExtent();
                const center = getCenter(extent);
                centerPoint = new Point(center);
            }
            
            const centerCoords = centerPoint.getCoordinates();
            return transform(centerCoords, 'EPSG:3857', 'EPSG:4326');
        } catch (error) {
            console.error('Error calculating centroid:', error);
            return null;
        }
    };

    // Función para convertir geometría a GeoJSON
    const geometryToGeoJSON = (geometry) => {
        if (!geometry) return null;
        
        try {
            const format = new GeoJSON();
            return format.writeGeometry(geometry, {
                dataProjection: 'EPSG:4326',
                featureProjection: 'EPSG:3857'
            });
        } catch (error) {
            console.error('Error converting to GeoJSON:', error);
            return null;
        }
    };

    // Funciones de manejo del mapa
    const handleClickMap = (event) => {
        if (geometryType !== 'Point') return; // Solo para puntos
        
        const coordinates3857 = event.coordinate; // Coordenadas en EPSG:3857 por defecto
        const coordinates4326 = transform(coordinates3857, 'EPSG:3857', 'EPSG:4326'); // Convertir a EPSG:4326
        console.log('Coordinates (EPSG:4326):', coordinates4326);
        closeToast('stepsAddIncidence');
        // Guardar las coordenadas convertidas en EPSG:4326
        setMousePosition({ x: coordinates4326[0], y: coordinates4326[1] });
        
        // Crear geometría Point
        const pointGeometry = new Point(coordinates3857);
        const geoJSON = geometryToGeoJSON(pointGeometry);
        setDrawnGeometry(geoJSON);
        
        setShowText(false);
        setShowModal(true);
        // Resetear el estado de archivos para la nueva incidencia
        setUploadedFiles([]);
    };

    // Manejar el final del dibujo (para líneas y polígonos)
    const handleDrawEnd = (event) => {
        const geometry = event.feature.getGeometry();
        if (!geometry) return;

        closeToast('stepsAddIncidence');
        
        // Calcular centroide
        const centroid = getCentroidFromGeometry(geometry);
        if (centroid) {
            setMousePosition({ x: centroid[0], y: centroid[1] });
        }

        // Convertir a GeoJSON
        const geoJSON = geometryToGeoJSON(geometry);
        setDrawnGeometry(geoJSON);

        setShowText(false);
        setShowModal(true);
        setUploadedFiles([]);
    };

    const handleContextMenuClickMap = (event) => {
        event.preventDefault();
        setToolState('addIncidence', { active: false });
    };

    // Función para cerrar otras herramientas
    function closeAnothersTools() {
        if (toolStates?.attributeQueryByBufferBox?.active) {
            setToolState('attributeQueryByBufferBox', { active: false });
        }
        if (toolStates?.addQuote?.active) {
            setToolState('addQuote', { active: false });
        }
    }

    // Función para limpiar el dibujo
    const cleanupDraw = () => {
        if (!map) return;

        if (drawRef.current) {
            if (drawRef.current._cleanup) {
                drawRef.current._cleanup();
            }
            map.removeInteraction(drawRef.current);
            drawRef.current = null;
        }

        if (drawLayerRef.current) {
            map.removeLayer(drawLayerRef.current);
            drawLayerRef.current = null;
        }

        if (drawSourceRef.current) {
            drawSourceRef.current.clear();
        }
    };

    // Función para activar el dibujo según el tipo de geometría
    const activateDraw = () => {
        if (!map) return;

        cleanupDraw();

        // Crear capa para el dibujo temporal
        const drawSource = drawSourceRef.current;
        const drawLayer = new VectorLayer({
            source: drawSource,
            style: new Style({
                fill: new Fill({
                    color: 'rgba(220, 53, 69, 0.2)'
                }),
                stroke: new Stroke({
                    color: '#dc3545',
                    width: 2
                }),
                image: new CircleStyle({
                    radius: 5,
                    fill: new Fill({
                        color: '#dc3545'
                    })
                })
            }),
            zIndex: 1000
        });

        drawLayerRef.current = drawLayer;
        map.addLayer(drawLayer);

        if (geometryType === 'Point') {
            // Para puntos, usar el click del mapa directamente
            return;
        }

        // Para líneas y polígonos, usar Draw
        const draw = new Draw({
            source: drawSource,
            type: geometryType,
            style: new Style({
                fill: new Fill({
                    color: 'rgba(220, 53, 69, 0.2)'
                }),
                stroke: new Stroke({
                    color: '#dc3545',
                    width: 2
                }),
                image: new CircleStyle({
                    radius: 5,
                    fill: new Fill({
                        color: '#dc3545'
                    })
                })
            })
        });

        draw.on('drawend', handleDrawEnd);

        // Manejar tecla Escape para cancelar
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                drawSource.clear();
                if (drawRef.current) {
                    map.removeInteraction(drawRef.current);
                    map.addInteraction(drawRef.current);
                }
            }
        };

        // Manejar click derecho para cancelar
        const handleContextMenu = (e) => {
            e.preventDefault();
            if (drawRef.current) {
                drawRef.current.finishDrawing();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        const viewport = map.getViewport();
        viewport.addEventListener('contextmenu', handleContextMenu);

        draw._cleanup = () => {
            window.removeEventListener('keydown', handleKeyDown);
            viewport.removeEventListener('contextmenu', handleContextMenu);
        };

        drawRef.current = draw;
        map.addInteraction(draw);
    };

    // Efectos
    useEffect(() => {
        if (toolStates.addIncidence && toolStates.addIncidence.active) {
            if (!map) return;
            closeAnothersTools();
            setShowText(true);
            messageShowSteps();
            
            // Limpiar geometría previa
            setDrawnGeometry(null);
            cleanupDraw();
            
            if (geometryType === 'Point') {
                map.on('singleclick', handleClickMap);
                map.on('contextmenu', handleContextMenuClickMap);
            } else if (geometryType === 'Polygon') {
                activateDraw();
            }
            
            return () => {
                map.un('singleclick', handleClickMap);
                map.un('contextmenu', handleContextMenuClickMap);
                cleanupDraw();
                closeToast('stepsAddIncidence');
            };
        } else {
            setShowText(false);
            cleanupDraw();
        }
    }, [toolStates, map, geometryType]);

    const onSubmit = async (data) => {
        try {
            // Preparar los datos de la incidencia
            const incidencePayload = {
                ...data,
                map_viewer_id: getVisorID(),
                comment: data.comment,
                xy_coordinates: {
                    x: mousePosition.x,
                    y: mousePosition.y,
                },
                location: `${mousePosition.x},${mousePosition.y}`,
                status: 'pending',
                created_by: session.user?.id || null,
            };

            // Incluir la geometría completa en formato GeoJSON si existe
            if (drawnGeometry) {
                try {
                    const geometryObj = JSON.parse(drawnGeometry);
                    incidencePayload.geometry = geometryObj;
                    incidencePayload.geometry_type = geometryType;
                } catch (error) {
                    console.error('Error parsing geometry:', error);
                }
            }

            const response = await createIncidence(
                incidencePayload,
                { signal: AbortSignal.timeout(10000) }
            );

            if (response.ok) {
                const responseData = await response.json();
                console.log('Incidencia creada con éxito:', responseData);
                messageIncidenceAddSuccess(responseData);
                
                // Añadir la nueva incidencia directamente a la capa de incidencias pendientes
                if (map) {
                    const geometry = drawnGeometry ? JSON.parse(drawnGeometry) : null;
                    addIncidenceToLayer(
                        map, 
                        responseData, 
                        [mousePosition.x, mousePosition.y], 
                        geometryType, 
                        geometry
                    );
                }
            } else {
                // Intentar obtener el mensaje de error del servidor
                let errorMessage = response.statusText;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorData.error || errorMessage;
                } catch (e) {
                    // Si no se puede parsear el JSON, usar el statusText
                }
                console.error('Error al crear la incidencia:', errorMessage);
                messageIncidenceAddError(errorMessage);
            }
        } catch (error) {
            console.error('Error inesperado al crear la incidencia:', error);
            messageIncidenceAddError(t('An unexpected error occurred. Please try again.'));
        } finally {
            setShowModal(false);
            setShowText(false);
            setToolState('addIncidence', { active: false });
            // Resetear el estado de archivos después de enviar
            setUploadedFiles([]);
            setDrawnGeometry(null);
            cleanupDraw();
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setShowText(true);
        // Resetear el estado de archivos al cerrar
        setUploadedFiles([]);
        // Limpiar el dibujo pero mantener la herramienta activa
        if (geometryType !== 'Point') {
            if (drawSourceRef.current) {
                drawSourceRef.current.clear();
            }
        }
    };

    const getMouseFollowerText = () => {
        switch (geometryType) {
            case 'Point':
                return t('Click on the map to add a new incidence');
            case 'Polygon':
                return t('Draw a polygon on the map. Double click to finish.');
            default:
                return t('Click on the map to add a new incidence');
        }
    };

    // Función para cambiar el tipo de geometría
    const handleGeometryTypeChange = (newType) => {
        setGeometryType(newType);
        setDrawnGeometry(null);
        cleanupDraw();
        
        // Si la herramienta está activa, reactivarla con el nuevo tipo
        if (toolStates.addIncidence?.active) {
            // No necesitamos desactivar y reactivar, solo limpiar y dejar que el useEffect maneje el cambio
        }
    };

    return (
        <>
            <MouseFollower text={getMouseFollowerText()} show={showText} />
            <Tool
                id="addIncidence"
                icon="bi bi-exclamation-triangle incidence-icon-with-plus"
                label={t('Add Incidence')}
            />
            {/* Selector de tipo de geometría - visible cuando la herramienta está activa */}
            {toolStates.addIncidence?.active && (
                <div style={{
                    position: 'fixed',
                    top: '80px',
                    left: '70px',
                    backgroundColor: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '200px',
                    border: '1px solid #dee2e6'
                }}>
                    <label className="form-label" style={{ 
                        marginBottom: '10px', 
                        fontSize: '13px', 
                        fontWeight: '600',
                        display: 'block',
                        color: '#495057'
                    }}>
                        {t('Geometry type')}
                    </label>
                    <div className="btn-group w-100" role="group">
                        <input
                            type="radio"
                            className="btn-check"
                            name="geometryType"
                            id="geometryTypePoint"
                            value="Point"
                            checked={geometryType === 'Point'}
                            onChange={(e) => handleGeometryTypeChange(e.target.value)}
                        />
                        <label className="btn btn-outline-primary" htmlFor="geometryTypePoint" style={{ 
                            fontSize: '13px',
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                        }}>
                            <i className="bi bi-geo-alt"></i>
                            <span>{t('Point')}</span>
                        </label>

                        <input
                            type="radio"
                            className="btn-check"
                            name="geometryType"
                            id="geometryTypePolygon"
                            value="Polygon"
                            checked={geometryType === 'Polygon'}
                            onChange={(e) => handleGeometryTypeChange(e.target.value)}
                        />
                        <label className="btn btn-outline-primary" htmlFor="geometryTypePolygon" style={{ 
                            fontSize: '13px',
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                        }}>
                            <i className="bi bi-pentagon"></i>
                            <span>{t('Polygon')}</span>
                        </label>
                    </div>
                </div>
            )}
            <Modal show={showModal} onClose={handleCloseModal} title={t('Add Incidence')}>
                <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs} />
                {/* En lugar de desmontar los componentes los ocultamos */}
                <div className='mt-3' style={{ display: activeTab === 'datos' ? 'block' : 'none' }}>
                    <IncidenceForm
                        onSubmit={onSubmit}
                        isUploading={isUploading}
                        uploadedFilesCount={uploadedFiles.length}
                    />
                </div>
                {/* <div className='mt-3' style={{ display: activeTab === 'adjuntos' ? 'block' : 'none' }}>
                    <FileUpload
                        onFilesChange={setUploadedFiles}
                        isUploading={setIsUploading}
                    />
                </div> */}
            </Modal>
        </>
    );
};

export default AddIncidence;

